import type { FC } from 'react';
import { useId, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack, useTheme } from '@mui/material';

import type { DashboardWidgetProps } from 'src/types/dashboard';
import { apiPaths } from 'src/services/api/apiPaths';
import WidgetFrame from 'src/components/dashboard/DashboardPage/components/WidgetFrame';
import type { AggregateList } from 'src/components/followUp/common/types';
import NoResultsToShowAdjustFiltersWarning from 'src/components/followUp/common/warning/NoResultsToShowAdjustFiltersWarning';
import WidgetFooterLegend from 'src/components/followUp/common/WidgetFooterLegend/WidgetFooterLegend';
import WidgetHeaderWithPill from 'src/components/followUp/common/WidgetHeaderWithPill/WidgetHeaderWithPill';
import useGenerateWidgetEndpoint from 'src/components/followUp/hooks/useGenerateWidgetEndpoint';
import useWidgetQuery from 'src/components/followUp/hooks/useWidgetQuery';

import { mapAggregateListToBarChartData } from './helper';
import { useExportOptions } from './hooks';
import NpsDistributionChart from './NpsDistributionChart';

const NpsDistributionWidget: FC<DashboardWidgetProps> = ({ config }) => {
  const ariaId = useId();
  const { colors } = useTheme();
  const { t } = useTranslation('followUp');
  const translatedLabels = useMemo(
    () => ({
      promoter: t('npsGroup.Promoter'),
      passive: t('npsGroup.Passive'),
      detractor: t('npsGroup.Detractor'),
    }),
    [t],
  );

  const stackLabels = [
    {
      name: translatedLabels.promoter,
      color: colors.tertiary[700],
    },
    {
      name: translatedLabels.passive,
      color: colors.neutral[500],
    },
    {
      name: translatedLabels.detractor,
      color: colors.error[600],
    },
  ];

  const endpoint = useGenerateWidgetEndpoint(
    apiPaths.WIDGET_DATA.NPS_DISTRIBUTION,
    config.id,
  );

  const { data, isFetching, errorMsg, refetch, comparisonData, queryFilters } =
    useWidgetQuery<AggregateList>(endpoint);

  const barChartData = useMemo(
    () =>
      mapAggregateListToBarChartData(data, comparisonData, translatedLabels),
    [data, comparisonData, translatedLabels],
  );

  const exportOptions = useExportOptions({
    data,
    comparisonData,
    widgetName: config.name,
    queryFilters,
  });

  return (
    <WidgetFrame
      ariaId={ariaId}
      refetch={refetch}
      error={errorMsg}
      loading={isFetching}
      exportOptions={exportOptions}
      header={<WidgetHeaderWithPill title={config.name} ariaId={ariaId} />}
      footer={
        data?.totalObjects === 0 ? (
          <Stack
            sx={{
              height: '100%',
              justifyContent: 'center',
              alignItems: 'flex-start',
              px: 3,
            }}
          >
            <NoResultsToShowAdjustFiltersWarning />
          </Stack>
        ) : (
          <WidgetFooterLegend labels={stackLabels} />
        )
      }
    >
      {<NpsDistributionChart bars={barChartData} stackLabels={stackLabels} />}
    </WidgetFrame>
  );
};

export default NpsDistributionWidget;
